import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Collapse, Typography, useTheme } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import clsx from 'clsx';

import { IProgramSet } from '../../../types/interfaces/program-set';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { isEqual, uniqBy } from '../../../utils/collections/array-utils';
import { getSetKey } from '../../../utils/training/set-utils';
import DeleteActionButton from '../../designe/ActionButtons/DeleteActionButton';
import DoubleArrowDownActionButton from '../../designe/ActionButtons/DoubleArrowDownActionButton';
import DoubleArrowUpActionButton from '../../designe/ActionButtons/DoubleArrowUpActionButton';
import ExpandActionButton from '../../designe/ActionButtons/ExpandActionButton';
import AppDashedBlockButton from '../../designe/buttons/block-buttons/AppDashedBlockButton';
import AppDialog from '../app-dialog/AppDialog';
import SavedProgramSet from '../saved-program-set-modal/SavedProgramSet';
import SetEdit from './SetEdit/SetEdit';
import { useStyles } from './SetGroupEditTable.styles';
import { IUseSetGroupEditTable } from './interfaces';
import { useSetGroupEditComponent } from './useSetGroupEditComponent';

interface ISetGroupEditTableProps<T extends IProgramSet | ITrainingSet> {
  sets: T[];
  useSetGroupEditTable: IUseSetGroupEditTable<T>;
}

function SetGroupEditTableComponent<T extends IProgramSet | ITrainingSet>({
  useSetGroupEditTable,
  sets,
}: ISetGroupEditTableProps<T>) {
  const { options, maxSetNumber, onChangeSetOrder, onRemoveSetsBySetNumber } =
    useSetGroupEditTable;
  const theme = useTheme();
  const {
    expanded,
    setExpanded,
    isSuperset,
    onAddSetClick,
    onAddSavedProgramSet,
    showSavedProgramItemModal,
    setShowSavedProgramItemModal,
    exercises,
  } = useSetGroupEditComponent<T>(useSetGroupEditTable, sets);
  const { t } = useTranslation();
  const { classes } = useStyles({ hideFrame: !!options?.hideFrame });
  const currentSetNumber = sets[0].setNumber;

  const showRemoveSetButton =
    (options.removeSetMode === 'setGroup' ||
      options.removeSetMode === 'both' ||
      sets.length === 1) &&
    !!onRemoveSetsBySetNumber;

  const viewName = useMemo(() => {
    const uniqueExerciseTitles = uniqBy<T>(sets, 'exerciseId')
      .map((set) => exercises.find((e) => e.id === set?.exerciseId)?.title)
      .filter(Boolean)
      .join(', ');

    return uniqueExerciseTitles ? (
      <Typography variant="h3" color={theme.palette.font.secondary}>
        {isSuperset && (
          <Typography
            variant="h3"
            fontWeight={600}
            color={theme.palette.font.secondary}
            component="span"
            marginRight={0.5}>
            {t('setTable.superset')}:
          </Typography>
        )}
        {uniqueExerciseTitles}
      </Typography>
    ) : null;
  }, [sets, exercises]);

  return (
    <Box className={classes.setGroupsWrapper}>
      {!options.hideHeader && (
        <Box
          className={clsx(classes.setGroupHeader)}
          paddingBottom={isSuperset ? theme.spacing(2) : 0}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            {viewName}

            <Box
              display="flex"
              gap={1}
              alignItems="center"
              justifyContent="flex-end"
              flex={1}>
              {onChangeSetOrder && (
                <>
                  <DoubleArrowUpActionButton
                    data-onboarding="newTraining.sortSets"
                    onClick={() => onChangeSetOrder(currentSetNumber, 'up')}
                    disabled={currentSetNumber === 0}
                  />
                  <DoubleArrowDownActionButton
                    data-onboarding="newTraining.sortSets"
                    onClick={() => onChangeSetOrder(currentSetNumber, 'down')}
                    disabled={currentSetNumber === maxSetNumber}
                  />
                </>
              )}
              {showRemoveSetButton && (
                <DeleteActionButton
                  onClick={() => onRemoveSetsBySetNumber(currentSetNumber)}
                />
              )}

              {!options.hideExpand && (
                <ExpandActionButton
                  onClick={() => setExpanded(!expanded)}
                  expanded={expanded}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box className={clsx(classes.setGroupExpandable)}>
          {sets.map((set: IProgramSet | ITrainingSet) => (
            <React.Fragment key={set.id}>
              {isSuperset && <Box className={classes.supersetDivider} />}
              <SetEdit
                key={getSetKey(set)}
                set={set}
                setsCount={sets.length}
                isSuperset={isSuperset}
                useSetGroupEditTable={
                  useSetGroupEditTable as IUseSetGroupEditTable<
                    IProgramSet | ITrainingSet
                  >
                }
              />
            </React.Fragment>
          ))}

          {!options.hideSuperset && (
            <Box className={classes.setGroupFooter}>
              <AppDashedBlockButton
                data-onboarding="newTraining.addExerciseToSuperset"
                fullWidth
                yPadding={1}
                startIcon={<AddIcon />}
                onClick={() => onAddSetClick()}>
                {t('setTable.addExerciseToSuperset')}
              </AppDashedBlockButton>
              {!options.hideSavedSetUploading && (
                <AppDashedBlockButton
                  fullWidth
                  yPadding={1}
                  startIcon={<FileUploadIcon />}
                  onClick={() => setShowSavedProgramItemModal(true)}>
                  {t('setTable.loadSetInSuperset')}
                </AppDashedBlockButton>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
      <AppDialog
        open={!!showSavedProgramItemModal}
        onClose={() => setShowSavedProgramItemModal(false)}
        title={t('setTable.saveSet')}>
        <SavedProgramSet
          onCancel={() => setShowSavedProgramItemModal(false)}
          onSubmitSavedProgramSet={(set) => {
            setShowSavedProgramItemModal(false);
            onAddSavedProgramSet(set);
          }}
        />
      </AppDialog>
    </Box>
  );
}

const SetGroupEditTable = React.memo(
  SetGroupEditTableComponent,
  (prevProps, nextProps) =>
    isEqual(
      prevProps.useSetGroupEditTable.options,
      nextProps.useSetGroupEditTable.options,
    ) &&
    isEqual(prevProps.sets, nextProps.sets) &&
    // isEqual(prevProps.useSetGroupEditTable, nextProps.useSetGroupEditTable) &&
    prevProps.useSetGroupEditTable.maxSetNumber ===
      nextProps.useSetGroupEditTable.maxSetNumber &&
    prevProps.useSetGroupEditTable.submitCalled ===
      nextProps.useSetGroupEditTable.submitCalled,
) as <T extends IProgramSet | ITrainingSet>(
  props: ISetGroupEditTableProps<T>,
) => React.ReactElement;

export default SetGroupEditTable;
